import clsx from "classnames"
import { ContentSummaryTag } from "common/components/ContentSummaryTag"
import { Heading } from "common/components/Heading"
import { ImgixImage } from "common/components/ImgixImage"
import { closestCrop } from "modules/editor/utils/closestCrop"
import { ImageAspectRatio } from "modules/editor/types"
import type { ContentSummaryVariantProps } from "../types"
import { Image, Maybe } from "common/types/graphql"

interface BaseWideSummaryImageProps {
  image?: Maybe<Partial<Image>>
  title?: string
  imageSizes?: string
  imageFit?: "cover" | "contain"
}

/**
 * `BaseWideSummaryImage` is a functional component that renders an image with different crops based on the screen size.
 * For larger screens (2xl), a Pano crop is used, and for smaller screens, a square crop is used.
 */
export const BaseWideSummaryImage = ({
  image,
  imageSizes,
  imageFit,
}: BaseWideSummaryImageProps) => {
  if (image?.src === undefined) {
    return null
  }

  const squareCrop = closestCrop(ImageAspectRatio.Square, image.crops || [])
  const panoCrop = closestCrop(ImageAspectRatio.Pano, image.crops || [])

  const imageProps = {
    ...image,
    src: image.src,
    alt: image.alt,
    sizes: imageSizes,
    objectFit: imageFit,
  }

  return (
    <>
      <div className="relative col-span-1 hidden aspect-2/1 sm:col-span-2 2xl:block">
        <ImgixImage {...imageProps} crop={panoCrop} />
      </div>
      <div className="relative col-span-1 aspect-square sm:col-span-2 2xl:hidden">
        <ImgixImage {...imageProps} crop={squareCrop} />
      </div>
    </>
  )
}

// Basewide no longer supports overriding the image aspect ratio
export type BaseWideVariantProps = Omit<ContentSummaryVariantProps, "desktopImageAspectRatio">
export const BaseWide = ({
  title,
  url,
  byline,
  hideByline,
  contentType,
  imageSizes,
  image,
  imageFit,
  tagLabel,
  tagStyle,
  anchorTarget,
  className,
}: BaseWideVariantProps) => {
  return (
    <a
      href={url}
      target={anchorTarget}
      className={clsx(
        "group grid h-fit w-full grid-cols-3 gap-x-4 gap-y-2 sm:grid-cols-6 2xl:gap-x-8",
        className,
      )}
    >
      <section className="col-span-2 gap-2 sm:col-span-4">
        <span className="group-hover:text-indigo-600 group-hover:underline">
          <Heading level={5}>
            <span dangerouslySetInnerHTML={{ __html: title || "" }} />
          </Heading>
        </span>
        {<ContentSummaryTag contentType={contentType} tagLabel={tagLabel} tagStyle={tagStyle} />}
        {byline && !hideByline && <p className="text-3xs line-clamp-3 text-gray-600">{byline}</p>}
      </section>

      <BaseWideSummaryImage image={image} imageSizes={imageSizes} imageFit={imageFit} />
    </a>
  )
}
